// TimesheetManagement.js
import React, { useState, useEffect } from "react";
import { apiGet, apiPut, apiPost, apiDelete } from "../../utils/apiFetch";
import pathObj from "utils/pathObj";
import apiPath from "utils/apiPath";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import useToastContext from "hooks/useToastContext";
import Pagination from "../Pagination";
import TimesheetDetailModal from "./TimesheetDetailModal";
import TimesheetEditModal from "./TimesheetEditModal";
import helpers from "utils/helpers";
import { useTranslation } from "react-i18next";
import {
  FiRefreshCw,
  FiLoader,
  FiDownload,
} from "react-icons/fi";
import TimesheetFilters from "./TimesheetFilters";
import TimesheetTable from "./TimesheetTable";
import { exportToExcel } from "./TimesheetExport";

// Extend dayjs with isoWeek plugin
dayjs.extend(isoWeek);

const TimesheetManagement = () => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [timesheets, setTimesheets] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({
    status: "approved",
    startDate: "",
    endDate: "",
    employeeId: "",
  });
  const [selectedTs, setSelectedTs] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDayIndex, setEditDayIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Fetch employee list for dropdown
  const fetchEmployeeList = async () => {
    try {
      const res = await apiGet(apiPath.aremployeeListHR);
      if (res?.data?.success) {
        const data = res?.data?.results || [];
        const options = data.map((emp) => ({
          label: `${emp.name} (${emp.employeeId || ""})`,
          value: emp._id,
        }));
        setEmployeeOptions(options);
      }
    } catch (error) {
      console.log("Error fetching employee list:", error);
    }
  };

  const fetchTimesheets = async () => {
    try {
      setIsLoading(true);
      const params = {
        page,
        pageSize: 10,
        status: filter.status,
        employeeId: filter.employeeId || undefined,
      };
      if (filter.startDate) params.startDate = filter.startDate;
      if (filter.endDate) params.endDate = filter.endDate;

      const res = await apiGet(pathObj.hrTimesheets, params);
      const data = res?.data?.results;
      setTimesheets(data?.docs || []);
      setPagination(data);
    } catch (error) {
      console.log("Error fetching timesheets:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployeeList();
    fetchTimesheets();
  }, [page, filter]);

  const handlePageClick = (event) => {
    setPage(event.selected + 1);
  };

  const handleApprove = async (id) => {
    helpers.alertFunction(
      "Are you sure you want to approve this timesheet?",
      id,
      async (timesheetId) => {
        try {
          const res = await apiPut(
            `${pathObj.approveTimesheet}/${timesheetId}/approve`,
          );
          if (res?.data?.success) {
            notification.success(res?.data?.message || "Approved");
            fetchTimesheets();
          } else {
            notification.error(res?.data?.message);
          }
        } catch (error) {
          notification.error("Something went wrong");
        }
      },
      true,
    );
  };

  const handleReject = (id) => {
    const reason = prompt("Please enter rejection reason:");
    if (!reason) return;

    helpers.alertFunction(
      `Reject this timesheet? Reason: ${reason}`,
      { id, reason },
      async (data) => {
        try {
          const res = await apiPut(
            `${pathObj.rejectTimesheet}/${data.id}/reject`,
            { reason: data.reason },
          );
          if (res?.data?.success) {
            notification.success(res?.data?.message || "Rejected");
            fetchTimesheets();
          } else {
            notification.error(res?.data?.message);
          }
        } catch (error) {
          notification.error("Something went wrong");
        }
      },
      true,
    );
  };

  const handleView = (ts) => {
    setSelectedTs(ts);
    setShowDetailModal(true);
  };

  const handleEdit = (ts, dayIndex) => {
    setSelectedTs(ts);
    setEditDayIndex(dayIndex);
    setShowEditModal(true);
  };

  const handleGenerate = async () => {
    try {
      setIsLoading(true);
      const res = await apiPost(pathObj.generateTimesheets, {
        date: dayjs().format(),
      });
      if (res?.data?.success) {
        notification.success(res?.data?.message || "Timesheets generated");
        fetchTimesheets();
      } else {
        notification.error(res?.data?.message);
      }
    } catch (error) {
      notification.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (id) => {
    helpers.alertFunction(
      "Are you sure you want to delete this timesheet?",
      id,
      async (timesheetId) => {
        try {
          const res = await apiDelete(
            `${pathObj.deleteTimesheet}/${timesheetId}`,
          );
          if (res?.data?.success) {
            notification.success(res?.data?.message || "Deleted");
            fetchTimesheets();
          } else {
            notification.error(res?.data?.message);
          }
        } catch (error) {
          notification.error("Something went wrong");
        }
      },
      true,
    );
  };

  const handleDateChange = (start, end) => {
    setFilter({ ...filter, startDate: start, endDate: end });
    setPage(1);
  };

  const handleReset = () => {
    setFilter({
      status: "approved",
      startDate: "",
      endDate: "",
      employeeId: "",
      isReset: true  
    });
    setSelectedEmployee(null);
    setPage(1);
  };

  const handleExport = () => {
    if (timesheets.length === 0) {
      notification.warning("No data to export");
      return;
    }
    exportToExcel(timesheets, formatWeekDisplay, formatTime, getAttendanceIcon);
    notification.success("Timesheets exported successfully");
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: { bg: "bg-yellow-100 text-yellow-800", text: "Draft" },
      submitted: { bg: "bg-blue-100 text-blue-800", text: "Submitted" },
      approved: { bg: "bg-green-100 text-green-800", text: "Approved" },
      rejected: { bg: "bg-red-100 text-red-800", text: "Rejected" },
    };
    const v = variants[status] || variants.draft;
    return (
      <span
        className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${v.bg}`}
      >
        {v.text}
      </span>
    );
  };

  const getAttendanceIcon = (entry) => {
    // Check for Leave of Absence first
    if (entry?.isOnLeave) {
      if (entry.leaveType === 'paid_leave_of_absence') {
        return <span title="Paid Leave" className="text-green-600 font-bold text-xs">PL</span>;
      }
      if (entry.leaveType === 'unpaid_leave_of_absence') {
        return <span title="Unpaid Leave" className="text-orange-600 font-bold text-xs">UL</span>;
      }
      if (entry.leaveType === 'paid_leave') {
        return <span title="Paid Leave" className="text-blue-600 font-bold text-xs">PL</span>;
      }
      if (entry.leaveType === 'unpaid_leave') {
        return <span title="Unpaid Leave" className="text-red-600 font-bold text-xs">UL</span>;
      }
      return <span title="Leave" className="text-purple-600 font-bold text-xs">LV</span>;
    }
    
    if (entry?.isAbsent)
      return <span title="Absent" className="text-red-600 font-bold">A</span>;
    if (entry?.isLate && entry?.isEarly)
      return <span title="Late and Early" className="text-yellow-600">⚠</span>;
    if (entry?.isLate) return <span title="Late" className="text-yellow-600">L</span>;
    if (entry?.isEarly) return <span title="Early" className="text-blue-600">E</span>;
    return <span className="text-green-600">✓</span>;
  };

  // Fixed: Correct week display - week ends on Saturday (day 6)
  const formatWeekDisplay = (startDate, endDate) => {
    const start = dayjs(startDate);
    // Calculate the correct end date (Saturday of the same week)
    const correctEndDate = start.add(6, 'day'); // Monday + 6 days = Sunday
    const end = dayjs(endDate);
    
    // Use the correct end date for display
    return `${start.format("MMM D")} - ${correctEndDate.format("MMM D")}`;
  };

  const formatTime = (date) => {
    if (!date) return "-";
    return dayjs(date).format("HH:mm");
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="px-4 py-4 max-w-7xl mx-auto">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-lg text-gray-800">
              {t("TIMESHEET_MANAGEMENT")}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
                disabled={isLoading || timesheets.length === 0}
              >
                <FiDownload className="w-4 h-4" />
                {t("EXPORT")}
              </button>
              <button
                onClick={handleGenerate}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
                disabled={isLoading}
              >
                {isLoading ? (
                  <FiLoader className="animate-spin w-4 h-4" />
                ) : (
                  <FiRefreshCw className="w-4 h-4" />
                )}
                {t("GENERATE_WEEKLY_TIMESHEETS")}
              </button>
            </div>
          </div>

          {/* Filters */}
          <TimesheetFilters
            filter={filter}
            setFilter={setFilter}
            employeeOptions={employeeOptions}
            selectedEmployee={selectedEmployee}
            setSelectedEmployee={setSelectedEmployee}
            handleDateChange={handleDateChange}
            handleReset={handleReset}
            isLoading={isLoading}
          />

          {/* Table */}
          <TimesheetTable
            timesheets={timesheets}
            pagination={pagination}
            page={page}
            isLoading={isLoading}
            handleView={handleView}
            handleApprove={handleApprove}
            handleReject={handleReject}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            getStatusBadge={getStatusBadge}
            getAttendanceIcon={getAttendanceIcon}
            formatWeekDisplay={formatWeekDisplay}
            formatTime={formatTime}
          />

          {pagination?.totalPages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <Pagination
                handlePageClick={handlePageClick}
                options={{
                  ...pagination,
                  totalItems: pagination.totalDocs, // <--- FIX: Map the API field to what the component expects
                  pageCount: pagination.totalPages // <--- Also explicitly pass pageCount for safety
                }}
                page={page}
              />
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <TimesheetDetailModal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        timesheet={selectedTs}
        getAttendanceIcon={getAttendanceIcon}
        formatTime={formatTime}
        formatWeekDisplay={formatWeekDisplay}
      />

      {/* Edit Modal */}
      <TimesheetEditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        timesheet={selectedTs}
        dayIndex={editDayIndex}
        refreshList={fetchTimesheets}
      />
    </div>
  );
};

export default TimesheetManagement;